import { alertService, createStorePersist, RequestStatus } from "common-frontend-components";
import { useEffect } from "react";
import { productCategoryAssociationsStore } from "./productCategoryAssociationsStore";
import { getStorageCall } from "@yehonadav/safestorage";
import { categoriesStore } from "../useCategoriesStore";
import { callGetProductsAsync, setProductDates, sortProducts } from "../../apis/products";
const getDefaultNewProduct = () => ({
    title: '',
    description: '',
    categories: [],
    files: [],
    links: [],
    index: undefined,
});
const persistOptions = {
    name: `ramibarilan/products/store`,
    whitelist: ["products"],
    getStorage: getStorageCall,
};
const { useStore, fetchStore, set, get } = createStorePersist({
    persistOptions,
    persistAfterClearingStorage: false,
    getDefaultValues: () => ({
        products: {},
        productList: [],
        newProduct: getDefaultNewProduct(),
        dataFiltered: [],
        pageIndex: 0,
        rowsPerPage: 25,
        pageItems: [],
        requestStatus: RequestStatus.pending,
    })
});
const getProducts = () => get().productList;
const setProducts = (productList) => {
    const products = {};
    productList.forEach(product => { products[product.id] = product; });
    set({ products, productList });
};
// set products from storage
setProducts(Object.values(get().products).map(i => {
    setProductDates(i);
    return i;
}));
const getDataFiltered = () => get().dataFiltered;
const getPageIndex = () => get().pageIndex;
const getRowsPerPage = () => get().rowsPerPage;
const getPageItems = () => get().pageItems;
const setDataFiltered = (dataFiltered) => set({ dataFiltered });
const setPageIndex = (pageIndex) => set({ pageIndex });
const setRowsPerPage = (rowsPerPage) => set({ rowsPerPage });
const setPageItems = (pageItems) => set({ pageItems });
const handleChangePage = (_event, newPage) => {
    setPageIndex(newPage);
};
const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageIndex(0);
};
const _getPageItems = () => {
    const rowsPerPage = getRowsPerPage();
    const skip = getPageIndex() * rowsPerPage;
    return getDataFiltered().slice(skip, skip + rowsPerPage);
};
const useProducts = () => useStore(fetchStore.productList);
const useDataFiltered = () => useStore(fetchStore.dataFiltered);
const usePageItems = () => useStore(fetchStore.pageItems);
const usePageIndex = () => useStore(fetchStore.pageIndex);
const useRowsPerPage = () => useStore(fetchStore.rowsPerPage);
const useFilterData = () => {
    const categoryFilters = categoriesStore.useCategoryFilters();
    const data = useProducts();
    useEffect(() => {
        let filteredData = data;
        if (categoryFilters.length > 0) {
            filteredData = [];
            categoryFilters.forEach(category => {
                const products = productCategoryAssociationsStore.getCategoryProducts(category.id);
                products.forEach(product => {
                    if (!filteredData.includes(product))
                        filteredData.push(product);
                });
            });
        }
        setDataFiltered(sortProducts(filteredData));
    }, [
        data,
        categoryFilters,
    ]);
};
const useGetPageItems = () => {
    const dataFiltered = useDataFiltered();
    const pageIndex = usePageIndex();
    const rowsPerPage = useRowsPerPage();
    useEffect(() => {
        setPageItems(_getPageItems());
    }, [dataFiltered, pageIndex, rowsPerPage]);
};
export const productsStore = {
    clearNewProduct: () => set({ newProduct: getDefaultNewProduct() }),
    getNewProduct: () => get().newProduct,
    setNewProduct: (newProduct) => set({ newProduct }),
    setNewProductPreviewImg: (preview) => set(s => ({ newProduct: { ...s.newProduct, preview } })),
    setNewProductLinks: (links) => set(s => ({ newProduct: { ...s.newProduct, links } })),
    setNewProductFiles: (files) => set(s => ({ newProduct: { ...s.newProduct, files } })),
    setNewProductPrice: (price) => set(s => ({ newProduct: { ...s.newProduct, price } })),
    setNewProductSale: (sale) => set(s => ({ newProduct: { ...s.newProduct, sale } })),
    setNewProductSaleStartDate: (saleStartDate) => set(s => ({ newProduct: { ...s.newProduct, saleStartDate } })),
    setNewProductSaleStopDate: (saleStopDate) => set(s => ({ newProduct: { ...s.newProduct, saleStopDate } })),
    setNewProductIndex: (index) => set(s => ({ newProduct: { ...s.newProduct, index } })),
    onNewProductCategoryChange: (_event, categories) => set(s => ({ newProduct: { ...s.newProduct, categories } })),
    useNewProductLinks: () => useStore(s => s.newProduct.links),
    useNewProductCategories: () => useStore(s => s.newProduct.categories),
    useNewProductTitle: () => useStore(s => s.newProduct.title),
    useNewProductPreviewImg: () => useStore(s => s.newProduct.preview),
    useNewProductIndex: () => useStore(s => s.newProduct.index),
    handleNewProductTitleChange: (e) => set(s => ({ newProduct: { ...s.newProduct, title: e.target.value } })),
    useNewProductDescription: () => useStore(s => s.newProduct.description),
    handleNewProductDescriptionChange: (e) => set(s => ({ newProduct: { ...s.newProduct, description: e.target.value } })),
    useNewProductFiles: () => useStore(s => s.newProduct.files),
    useNewProductPrice: () => useStore(s => s.newProduct.price),
    useNewProductSale: () => useStore(s => s.newProduct.sale),
    useNewProductSaleStartDate: () => useStore(s => s.newProduct.saleStartDate),
    useNewProductSaleStopDate: () => useStore(s => s.newProduct.saleStopDate),
    getProductMap: () => get().products,
    getProducts,
    setProducts,
    useProducts,
    getDataFiltered,
    getPageIndex,
    getRowsPerPage,
    getPageItems,
    setDataFiltered,
    setPageIndex,
    setRowsPerPage,
    setPageItems,
    handleChangePage,
    handleChangeRowsPerPage,
    useDataFiltered,
    usePageItems,
    usePageIndex,
    useRowsPerPage,
    useFilterData,
    useGetPageItems,
    getRequestStatus: () => get().requestStatus,
    // tslint:disable-next-line:no-shadowed-variable
    setRequestStatus: (requestStatus) => { console.log({ requestStatus }); const s = get(); s.requestStatus = requestStatus; set({ requestStatus }); },
    useRequestStatus: () => useStore(fetchStore.requestStatus),
    useLoad: () => {
        const status = productsStore.useRequestStatus();
        const data = productsStore.useProducts();
        useEffect(() => {
            if (get().requestStatus !== RequestStatus.pending)
                return;
            productsStore.setRequestStatus(RequestStatus.loading);
            callGetProductsAsync()
                .then((items) => {
                productsStore.setProducts(items);
                productsStore.setRequestStatus(RequestStatus.loaded);
            })
                .catch((e) => {
                alertService.error(e);
                productsStore.setRequestStatus(RequestStatus.failed);
            });
        }, [status]);
        return { status, data };
    },
};
//# sourceMappingURL=productsStore.js.map